/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { sessionStorageProvider } from '@opengeoweb/shared';
import type { FavouritePreset } from './types';
import type {
  FavouritePresetType,
  UserPreferences,
} from './user-preferences/api';
import workspacePresetsFallback from './workspace-api/fakeApi/workspacePresetsFallback.json';
import type {
  WorkspacePresetFromBE,
  WorkspacePresetListItem,
} from '../store/workspace/types';
import type { FetchWorkspaceParams } from '../store/workspaceList/types';

/* 
 configureLibraryDefaults([LIB_KEY], {
  staleTime: 5 * 60 * 1000,
});  
*/

export const LIB_KEY = 'workspace-lib';

export const getPresetBaseUrl = (): string => {
  const config = sessionStorageProvider.getConfig();
  return config?.GW_PRESET_BACKEND_URL || '';
};

export const getIdFromUrl = (url: string): string =>
  url && /[^/]+$/.exec(url)![0];

const ERROR_NOT_FOUND = 'Request failed with status code 400';

export const hasNoPresetBackend = (): boolean =>
  !getPresetBaseUrl() && process.env.NODE_ENV !== 'test';

export const extractWorkspacePresetFromJSON = (
  workspacePresetId: string,
): WorkspacePresetFromBE => {
  const preset = (workspacePresetsFallback as WorkspacePresetFromBE[]).find(
    (element) => element.id === workspacePresetId,
  );
  if (!preset) {
    throw new Error(ERROR_NOT_FOUND);
  }
  return preset;
};

export const filterWorkspacePresetList = (
  params?: FetchWorkspaceParams,
): WorkspacePresetListItem[] => {
  const list = (workspacePresetsFallback as WorkspacePresetFromBE[]).map(
    (preset): WorkspacePresetListItem => ({
      id: preset.id,
      title: preset.title,
      scope: preset.scope,
      viewType: preset.viewType,
      abstract: preset.abstract ?? '',
      description: preset.description,
      username: preset.username,
    }),
  );

  if (!params) {
    return list;
  }

  return list.filter((preset) => {
    if (params.scope && preset.scope !== params.scope) {
      return false;
    }
    if (params.search) {
      const query = params.search.toLowerCase();
      return (
        preset.title.toLowerCase().includes(query) ||
        preset.abstract.toLowerCase().includes(query)
      );
    }
    return true;
  });
};

export const favouritePresetKey = (
  type: FavouritePresetType,
  preset: Pick<FavouritePreset, 'id'>,
): string => `${type}:${preset.id}`;

export const updateCachedFavourite = (
  client: QueryClient,
  queryKey: QueryKey,
  key: string,
  isFavourite: boolean,
): void => {
  client.setQueryData<UserPreferences>(queryKey, (preferences) => {
    const favourites = preferences?.favouritePresets ?? [];
    return {
      ...preferences,
      favouritePresets: isFavourite
        ? Array.from(new Set([...favourites, key]))
        : favourites.filter((value) => value !== key),
    };
  });
};
